/**
 * Main App Component for Telegram MiniApp
 * Complete routing for all three sections:
 * 1. Master Bot (Vendor Dashboard)
 * 2. Vendor Bot Admin (Shop Management)
 * 3. Vendor Bot Shop (Customer Shopping)
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import GlobalStyles from './styles/GlobalStyles';

// Master Bot Pages
import MasterDashboard from './pages/master/Dashboard';
import MyBots from './pages/master/MyBots';
import CreateBot from './pages/master/CreateBot';
import Subscription from './pages/master/Subscription';
import MasterWallet from './pages/master/Wallet';
import MasterReferrals from './pages/master/Referrals';
import MasterAnalytics from './pages/master/Analytics';

// Vendor Bot Admin Pages
import AdminDashboard from './pages/vendor-admin/Dashboard';
import Products from './pages/vendor-admin/Products';
import ProductForm from './pages/vendor-admin/ProductForm';
import AdminOrders from './pages/vendor-admin/Orders';
import AdminOrderDetail from './pages/vendor-admin/OrderDetail';
import Customers from './pages/vendor-admin/Customers';
import AdminAnalytics from './pages/vendor-admin/Analytics';
import Settings from './pages/vendor-admin/Settings';
import Marketing from './pages/vendor-admin/Marketing';
import AdminReferrals from './pages/vendor-admin/Referrals';

// Vendor Bot Shop Pages (Customer MiniApp)
import StoreFront from './pages/vendor-shop/StoreFront';
import ShopProductDetail from './pages/vendor-shop/ProductDetail';
import ShopCart from './pages/vendor-shop/Cart';
import ShopCheckout from './pages/vendor-shop/Checkout';
import ShopDashboard from './pages/vendor-shop/Dashboard';
import ShopOrderDetail from './pages/vendor-shop/OrderDetail';
import ShopPayment from './pages/vendor-shop/Payment';
import ShopProfile from './pages/vendor-shop/Profile';
import ShopWallet from './pages/vendor-shop/Wallet';

// Components
import Loading from './components/Loading';
import ErrorBoundary from './components/ErrorBoundary';
import { ShopLayout } from './components/customer';

// Context
import { AuthProvider } from './context/AuthContext';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize Telegram WebApp
    const tg = (window as any).Telegram?.WebApp;

    if (tg) {
      // Apply Telegram native theme colors to CSS variables
      const themeParams = tg.themeParams || {};
      const root = document.documentElement;

      // Helper function to apply theme
      const applyTheme = (params: any) => {
        if (params.bg_color) {
          root.style.setProperty('--tg-theme-bg-color', params.bg_color);
          root.style.setProperty('--bg-primary', params.bg_color);
        }
        if (params.text_color) {
          root.style.setProperty('--tg-theme-text-color', params.text_color);
          root.style.setProperty('--text-primary', params.text_color);
        }
        if (params.hint_color) {
          root.style.setProperty('--tg-theme-hint-color', params.hint_color);
          root.style.setProperty('--text-secondary', params.hint_color);
        }
        if (params.link_color) {
          root.style.setProperty('--tg-theme-link-color', params.link_color);
        }
        if (params.button_color) {
          root.style.setProperty('--tg-theme-button-color', params.button_color);
          root.style.setProperty('--accent-green', params.button_color);
        }
        if (params.button_text_color) {
          root.style.setProperty('--tg-theme-button-text-color', params.button_text_color);
        }
        if (params.secondary_bg_color) {
          root.style.setProperty('--tg-theme-secondary-bg-color', params.secondary_bg_color);
          root.style.setProperty('--bg-secondary', params.secondary_bg_color);
          root.style.setProperty('--bg-card', params.secondary_bg_color);
        }
      };

      // Apply initial theme
      applyTheme(themeParams);

      // Set header and background to match theme
      if (themeParams.bg_color) {
        try {
          tg.setHeaderColor(themeParams.bg_color);
          tg.setBackgroundColor(themeParams.bg_color);
        } catch (e) {
          console.warn('Could not set header/background color:', e);
        }
      }

      // Listen for theme changes
      if (typeof tg.onEvent === 'function') {
        tg.onEvent('themeChanged', () => {
          applyTheme(tg.themeParams || {});
        });
      }

      // Expand WebApp to full screen
      if (typeof tg.expand === 'function') {
        tg.expand();
      }

      // Mark as ready
      if (typeof tg.ready === 'function') {
        tg.ready();
      }
    }

    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ErrorBoundary>
      <GlobalStyles />
      <AuthProvider>
        <Router>
          <Routes>
          {/* Master Bot - Vendor Dashboard Routes */}
          <Route path="/master/dashboard" element={<MasterDashboard />} />
          <Route path="/master/bots" element={<MyBots />} />
          <Route path="/master/create-bot" element={<CreateBot />} />
          <Route path="/master/subscription" element={<Subscription />} />
          <Route path="/master/wallet" element={<MasterWallet />} />
          <Route path="/master/referrals" element={<MasterReferrals />} />
          <Route path="/master/analytics" element={<MasterAnalytics />} />

          {/* Vendor Bot - Admin Panel Routes */}
          <Route path="/vendor/:botUsername/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/vendor/:botUsername/admin/products" element={<Products />} />
          <Route path="/vendor/:botUsername/admin/product/:id" element={<ProductForm />} />
          <Route path="/vendor/:botUsername/admin/orders" element={<AdminOrders />} />
          <Route path="/vendor/:botUsername/admin/order/:orderId" element={<AdminOrderDetail />} />
          <Route path="/vendor/:botUsername/admin/customers" element={<Customers />} />
          <Route path="/vendor/:botUsername/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/vendor/:botUsername/admin/settings" element={<Settings />} />
          <Route path="/vendor/:botUsername/admin/marketing" element={<Marketing />} />
          <Route path="/vendor/:botUsername/admin/referrals" element={<AdminReferrals />} />

          {/* Vendor Bot - Customer Shop Routes (wrapped with ShopLayout for status check) */}
          <Route path="/shop/:botUsername" element={<ShopLayout><StoreFront /></ShopLayout>} />
          <Route path="/shop/:botUsername/product/:id" element={<ShopLayout><ShopProductDetail /></ShopLayout>} />
          <Route path="/shop/:botUsername/cart" element={<ShopLayout><ShopCart /></ShopLayout>} />
          <Route path="/shop/:botUsername/checkout" element={<ShopLayout><ShopCheckout /></ShopLayout>} />
          <Route path="/shop/:botUsername/payment" element={<ShopLayout><ShopPayment /></ShopLayout>} />
          <Route path="/shop/:botUsername/dashboard" element={<ShopLayout><ShopDashboard /></ShopLayout>} />
          <Route path="/shop/:botUsername/orders" element={<ShopLayout><ShopDashboard /></ShopLayout>} />
          <Route path="/shop/:botUsername/orders/:orderId" element={<ShopLayout><ShopOrderDetail /></ShopLayout>} />
          <Route path="/shop/:botUsername/profile" element={<ShopLayout><ShopProfile /></ShopLayout>} />
          <Route path="/shop/:botUsername/wallet" element={<ShopLayout><ShopWallet /></ShopLayout>} />

          {/* Default route */}
          <Route path="/" element={<Navigate to="/master/dashboard" replace />} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/master/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
